import {
  Box,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { PiArrowLeft, PiFloppyDisk, PiPaperPlaneTilt } from 'react-icons/pi';
import { InvoiceEditor } from '@/components/ManualInvoiceCreation';
import { useInvoiceCalculation } from '@/components/ManualInvoiceCreation/hooks/useInvoiceCalculation';
import {
  useManualInvoiceStore,
  useSelectedServices,
  usePaymentMethod,
  useInvoiceCurrency,
  useRecipientType,
  useIndividualDetails,
  useCorporateDetails,
  useInvoicingCompanyId,
} from '@/models/manual-invoice.store';
import { useCreateManualInvoice, useSaveInvoiceDraft } from '@/models/invoices';
import { ClientType } from '@/types';
import { listUrl } from '@/lib/listMemory';

export default function CreateInvoicePage() {
  const [, setLocation] = useLocation();
  const store = useManualInvoiceStore();
  const hasReset = useRef(false);

  // Get state for totals calculation
  const services = useSelectedServices();
  const paymentMethod = usePaymentMethod();
  const invoiceCurrency = useInvoiceCurrency();
  const recipientType = useRecipientType();
  const individualDetails = useIndividualDetails();
  const corporateDetails = useCorporateDetails();
  const invoicingCompanyId = useInvoicingCompanyId();

  const createInvoice = useCreateManualInvoice();
  const saveDraft = useSaveInvoiceDraft();

  // Calculate totals directly
  const recipientCountry =
    recipientType === ClientType.Individual
      ? individualDetails.residenceCountry
      : '';

  const totals = useInvoiceCalculation({
    services,
    invoiceCurrency,
    paymentMethod,
    invoicingCompany: null, // Will be determined by backend
    recipientCountry,
  });

  // Reset store on mount (only once)
  useEffect(() => {
    if (!hasReset.current) {
      hasReset.current = true;
      useManualInvoiceStore.getState().reset();
    }
  }, []);

  const validateForm = (): string[] => {
    const errors: string[] = [];

    if (recipientType === ClientType.Individual) {
      if (!individualDetails.fullName.trim()) {
        errors.push('Recipient name is required');
      }
    } else {
      if (!corporateDetails.companyName.trim()) {
        errors.push('Company name is required');
      }
    }

    if (services.length === 0) {
      errors.push('At least one service is required');
    }

    if (!invoicingCompanyId) {
      errors.push('Issuing company is required');
    }

    return errors;
  };

  const buildInvoicePayload = () => {
    return {
      paymentMethod: store.paymentMethod,
      invoiceCurrency: store.invoiceCurrency,
      recipientType: store.recipientType,
      recipientDetails:
        store.recipientType === ClientType.Individual
          ? store.individualDetails
          : store.corporateDetails,
      services: store.services.map((s) => ({
        serviceId: s.serviceId,
        price: s.price,
        currency: s.currency,
        quantity: s.quantity,
      })),
      invoicingCompanyId: store.invoicingCompanyId,
      totals: {
        dueAmount: totals.dueAmount,
        dueCurrency: totals.dueCurrency,
        total: totals.total,
        invoiceCurrency,
      },
    };
  };

  const handleSaveDraft = async () => {
    const payload = buildInvoicePayload();

    try {
      await saveDraft.mutateAsync(payload);
      notifications.show({
        title: 'Draft Saved',
        message: 'Invoice draft has been saved successfully',
        color: 'green',
      });
      setLocation(`~${listUrl('/invoices')}`);
    } catch (_error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save draft. Please try again.',
        color: 'red',
      });
    }
  };

  const handleIssueInvoice = async () => {
    const errors = validateForm();

    if (errors.length > 0) {
      notifications.show({
        title: 'Validation Error',
        message: errors.join('. '),
        color: 'red',
      });
      return;
    }

    const payload = buildInvoicePayload();

    try {
      await createInvoice.mutateAsync(payload);
      notifications.show({
        title: 'Invoice Created',
        message: 'Invoice has been created and issued successfully',
        color: 'green',
      });
      setLocation(`~${listUrl('/invoices')}`);
    } catch (_error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to create invoice. Please try again.',
        color: 'red',
      });
    }
  };

  const handleBack = () => {
    setLocation(`~${listUrl('/invoices')}`);
  };

  const isLoading = createInvoice.isPending || saveDraft.isPending;

  return (
    <Container size="lg" py="md">
      <Stack gap="md">
        {/* Header */}
        <Paper p="md" withBorder>
          <Group justify="space-between" align="center">
            <Group gap="md">
              <Button
                variant="subtle"
                size="sm"
                leftSection={<PiArrowLeft size={16} />}
                onClick={handleBack}
              >
                Back
              </Button>
              <Title order={3}>Create Manual Invoice</Title>
            </Group>

            <Group gap="xs">
              <Button
                variant="light"
                size="sm"
                leftSection={<PiFloppyDisk size={16} />}
                onClick={handleSaveDraft}
                loading={saveDraft.isPending}
                disabled={isLoading}
              >
                Save Draft
              </Button>
              <Button
                size="sm"
                leftSection={<PiPaperPlaneTilt size={16} />}
                onClick={handleIssueInvoice}
                loading={createInvoice.isPending}
                disabled={isLoading}
              >
                Issue Invoice
              </Button>
            </Group>
          </Group>
        </Paper>

        {/* Editor */}
        <Box>
          <InvoiceEditor />
        </Box>

        {/* Footer with totals summary */}
        {totals.dueAmount > 0 && (
          <Paper p="md" withBorder bg="blue.0">
            <Group justify="space-between" align="center">
              <Text size="sm" c="dimmed">
                Ready to issue
              </Text>
              <Group gap="md">
                <Text size="sm">
                  Amount Due:{' '}
                  <Text span fw={600} size="lg">
                    {totals.dueAmount.toFixed(2)} {totals.dueCurrency}
                  </Text>
                </Text>
              </Group>
            </Group>
          </Paper>
        )}
      </Stack>
    </Container>
  );
}
